import { Component, Host, h, Prop, State, Watch } from "@stencil/core";

import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(relativeTime);
import "dayjs/locale/nl";
dayjs.locale("nl");

import { ILiftStatusSensorDataResponse } from "../../utils/types";
import { sensorInfo } from "../../utils/sensorInfo";
import { includes, isEmpty } from "lodash-es";
import { getStore, ILiftStatusState } from "../../store";
import { ObservableMap } from "@stencil/store";

@Component({
  tag: "ls-status-item",
  styleUrl: "ls-status-item.css",
  shadow: true,
})
export class LsStatusItem {
  /**
   * The ID of the sensor item that is to be displayed.
   */
  @Prop() readonly sensorId: number;

  private store: ObservableMap<ILiftStatusState>;

  /**
   * The unique key that is used to identify store data.
   */
  @Prop() readonly idKey: string;

  /**
   * Specifies the IDs and labels of the sensors that are to be displayed.
   */
  private allowedIds = "500,501,502,503,514,515,590";

  // Split the string into an array and use typecasting to convert into numbers
  private sensorsArray = this.allowedIds.split(",").map(Number);

  /**
   * Handles a change of the sensor ID
   */
  @Watch("sensorId")
  watchPropHandler() {
    this.disconnectedCallback();
    this.componentWillLoad();
  }

  /**
   * The data received from the subscription is placed in this state.
   */
  @State() sensorData: string;

  /**
   * The timestamp of the last update
   */
  @State() timestamp: string;

  /**
   * Keeps track of the setInterval ID.
   */
  private timer: number | null;

  /**
   * The label for the data that is to be displayed. Retrieved from the central store.
   */
  private dataLabel: string;

  private updateData = (sensorData: ILiftStatusSensorDataResponse) => {
    // Reset timer to prevent unnecessary updates
    this.clearTimer();
    this.setTimer();

    // If sensorData is empty, we don't need to update the data
    if (isEmpty(sensorData)) return;

    // The sensor values of 501 and 514 and 515 are zero based, but we need to show them one based
    if (
      includes([501, 514, 515], this.sensorId) &&
      !isNaN(parseInt(sensorData.values[this.sensorId], 10))
    ) {
      this.sensorData = sensorData.values[this.sensorId] + 1;
    } else {
      this.sensorData = sensorData.values[this.sensorId];
    }

    // Calculate relative timestamp
    this.timestamp = dayjs(sensorData.updated[this.sensorId]).fromNow();
  };

  private setTimer() {
    // Used to update relative timestamps
    this.timer = window.setInterval(() => {
      this.updateData(this.store.state.currentSensorData);
    }, 20000);
  }

  private clearTimer() {
    window.clearInterval(this.timer);
    this.timer = null;
  }

  connectedCallback() {
    this.setTimer();
  }

  async componentWillLoad() {
    this.store = await getStore(this.idKey);

    if (!this.sensorsArray.includes(this.sensorId)) {
      this.dataLabel = "Sensor ID onbekend";
      this.sensorData = "Geldige opties:";
      this.timestamp = this.sensorsArray.join(", ");
      this.clearTimer();
      return;
    }

    // If timer is cleared we need to start it again
    if (this.timer === null) {
      this.setTimer();
    }

    this.dataLabel = sensorInfo.getLabel(this.sensorId);

    this.updateData(this.store.state.currentSensorData);

    this.store.onChange("currentSensorData", (sensorData) => {
      this.updateData(sensorData);
    });
  }

  render() {
    let errorMessage: boolean | string = false;

    if (!isEmpty(this.store.state.error.message)) {
      errorMessage = this.store.state.error.message;
      this.clearTimer();
    }

    return (
      <Host>
        <slot></slot>
        <dl class="ls-status-item">
          <dt class="label">
            {this.dataLabel || `Sensor #${this.sensorId || " not specified"}`}
          </dt>
          <dd class="value">
            {errorMessage === false
              ? sensorInfo.getValue(this.sensorData) || "-"
              : errorMessage}
          </dd>
          <dd class="timestamp">
            {errorMessage === false
              ? this.timestamp
              : "More information in the browser console"}
          </dd>
        </dl>
      </Host>
    );
  }

  disconnectedCallback() {
    this.clearTimer();
  }
}
